import type { ReactNode, SubmitEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export interface ActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  rootError?: string | undefined;
  isPending: boolean;
  submitLabel: string;
  submittingLabel: string;
  onSubmit: SubmitEventHandler<HTMLFormElement>;
  /** Nothing can be submitted yet (no template to switch to, nothing new to send). */
  submitDisabled?: boolean;
  wide?: boolean;
  children: ReactNode;
}

/** The frame every production action dialog shares: title, server error, fields, cancel / submit. */
export function ActionDialog({
  open,
  onOpenChange,
  title,
  description,
  rootError,
  isPending,
  submitLabel,
  submittingLabel,
  onSubmit,
  submitDisabled = false,
  wide = false,
  children,
}: ActionDialogProps) {
  const { t } = useTranslation('common');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', wide && 'sm:max-w-3xl')}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          {children}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || submitDisabled}>
              {isPending ? submittingLabel : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
